import Component from "../core/Component"
import { scrollToElement } from "../services/Scroll"
import { closest, triggerEvent } from "../utils/dom"
import Motion from "../utils/Motion"

const STATES = {
    OPEN: "is-open",
    OPENING: "is-opening",
    CLOSING: "is-closing",
}

const ACTIONS = {
    OPEN: "open",
    CLOSE: "close",
    TOGGLE: "toggle",
}

export default class ToggleBlock extends Component {
    constructor(element, options) {
        super(
            element,
            (options = {
                ...options,
            })
        )

        this.caller = {
            action: null,
            promise: {
                instance: null,
                reject: null,
                resolve: null
            },
            options: {
                shouldScroll: false,
            },
        }

        this.motion = null
        this.openClosePromise = null
    }

    prepare() {
        this.checkHash()

        this.motion = new Motion({
            current: 0,
            acceleration: .35,
            friction: .3,
            onProgress: this.handleMotionProgress,
            onEnd: this.handleMotionEnd,
        })

        if (this.initializedWithHash) {
            let parents = closest(this.element, '[data-component="ToggleBlock"]')
            parents.forEach((parent) => {
                const toggleBlock = Component.getFromElement(parent)
                toggleBlock.open({
                    shouldScroll: false,
                })
            })
            this.open()
        } else if (this.element.classList.contains("is-open")) {
            this.open({
                shouldScroll: false,
                shouldAnimate: false
            })
        }

        getAllOpenersForElement(this.element).forEach(opener => {
            opener.setAttribute('aria-controls', this.element.id)
            opener.setAttribute('aria-expanded', this.is(STATES.OPEN) ? 'true' : 'false')
        })
    }

    open(options) {
        if (this.element.classList.contains(STATES.OPENING)) {
            return this.caller.promise.instance
        }

        this.caller?.promise?.resolve?.()

        const groupName = this.element.dataset.toggleblockGroup
        if (groupName) {
            const toggleBlockElsInSameGroup = [
                ...document.querySelectorAll(`[data-component="ToggleBlock"][data-toggleblock-group="${groupName}"]`),
            ]
            toggleBlockElsInSameGroup
                .filter((toggleBlockEl) => toggleBlockEl !== this.element)
                .map((toggleBlockEl) => Component.getFromElement(toggleBlockEl))
                .filter(Boolean)
                .forEach((toggleBlock) => toggleBlock.close())
        }

        const caller = {
            promise: {
                instance: null,
                resolve: null,
                reject: null
            },
            options: {
                action: ACTIONS.OPEN,
                scrollMode: "auto",
                shouldScroll: true,
                shouldAnimate: true,
                ...options,
            },
        }

        this.caller = caller

        //pokud je uz otevrenej pres ccska nebo odpredtim, tak resolvnout
        if (isElementOpenByCss(this.element)) {
            this.processActionEnd(caller)
            return Promise.resolve()
        }

        caller.promise.instance = new Promise((resolve, reject) => {
            getAllOpenersForElement(this.element).forEach((opener) => {
                opener.setAttribute('aria-expanded','true')
                opener.classList.add("is-open")
            })
            this.element.classList.remove(STATES.CLOSING)
            this.element.classList.add(STATES.OPENING)
            this.element.classList.add(STATES.OPEN)
            this.element.parentNode.classList.add(STATES.OPEN)

            if (!caller.options.shouldAnimate) {
                this.motion.set({
                    current: 1,
                })
            }

            this.motion.set({
                to: 1,
            })
            this.motion.animate()

            caller.promise.resolve = resolve
            caller.promise.reject = reject
        })

        this.caller = caller
        return caller.promise.instance
    }

    toggle(openOptions, closeOptions) {
        if (this.element.classList.contains(STATES.OPEN) || this.element.classList.contains(STATES.OPENING)) {
            return this.close(closeOptions)
        } else {
            return this.open(openOptions)
        }
    }

    close(options) {
        if (this.element.classList.contains(STATES.CLOSING)) {
            return this.caller.promise.instance
        }

        this.caller?.promise?.resolve?.()

        const caller = {
            promise: {
                instance: null,
                resolve: null,
                reject: null
            },
            options: {
                action: ACTIONS.CLOSE,
                scrollMode: "auto",
                shouldScroll: false,
                shouldAnimate: true,
                ...options,
            },
        }

        this.caller = caller

        if (
            !(
                this.element.classList.contains(STATES.OPEN) ||
                this.element.classList.contains(STATES.OPENING) ||
                this.element.classList.contains(STATES.CLOSING)
            )
        ) {
            this.processActionEnd(caller)
            return Promise.resolve()
        }

        caller.promise.instance = new Promise((resolve, reject) => {
            getAllOpenersForElement(this.element).forEach((opener) => {
                opener.setAttribute('data-expanded','false')
                opener.classList.remove("is-open")
            })
            this.element.classList.remove(STATES.OPEN)
            this.element.classList.remove(STATES.OPENING)
            this.element.parentNode.classList.remove(STATES.OPEN)
            this.element.classList.add(STATES.CLOSING)

            this.motion.set({
                to: 0,
            })
            this.motion.animate()

            caller.promise.resolve = resolve
            caller.promise.reject = reject
        })

        this.caller = caller
        return this.caller.promise.instance
    }

    handleMotionProgress = (progress) => {
        if (progress < .01) {
            this.motion.set({
                current: 0
            })
        }

        if (progress > .99) {
            this.motion.set({
                current: 1
            })
        }
        this.element.style.maxHeight = `${Math.round(progress * this.element.scrollHeight)}px`
    }

    handleMotionEnd = () => {
        if (this.motion.current === 0) {
            triggerEvent(this.element, "toggleblock-change", {
                action: ACTIONS.CLOSE,
                // caller
            })

            this.element.classList.remove(STATES.CLOSING)
        }

        if (this.motion.current === 1) {
            triggerEvent(this.element, "toggleblock-change", {
                action: ACTIONS.OPEN,
                // caller
            })

            const componentEls = [...this.element.querySelectorAll('[data-component]')]
            componentEls.forEach(componentEl => Component.getFromElement(componentEl)?.resize?.())

            this.element.classList.remove(STATES.OPENING)
        }

        this.processActionEnd()
    }

    processActionEnd() {
        this.element.style.willChange = ``
        this.element.style.transition = ``
        this.element.style.maxHeight = ``

        if (!this.caller) {
            return
        }

        if (this.caller.options.shouldScroll) {
            scrollToElement(this.element.parentElement, { mode: this.caller.options.scrollMode })
        }

        this.caller = null
    }

    checkHash() {
        const url = window.location.href
        const id = url.substring(url.indexOf("#")).replace("#", "")

        if (id && id === this.element.id) {
            this.initializedWithHash = true
        }
    }
}

function getAllOpenersForElement(element) {
    return [...document.querySelectorAll(`[href="#${element.id}"]`)]
}

function isElementOpenByCss(element) {
    element.classList.contains(STATES.OPEN) ||
        element.offsetHeight === element.scrollHeight ||
        window.getComputedStyle(element).maxHeight === "100%"
}
